/**
 * /pages/Filials.js – Работа со настройками филиалов предприятия
 * /stores/filial.js – бизнес логика
 * 
 * Список филиалов доступен всем пользователям
 * Редактирование, добавление и удаление филиала может производить пользователь с правом «ManageFilials»
 * 
 * При загрузке страницы выполняется запрос «Filials» — получение филиалов активного предприятия
 * Также для справочников вызываются следующие методы один раз при загрузке страницы
 *  - «Cities» — список городов
 *  - «GetTZInfo» — список часовых поясов
 * Если предприятие — не колл-центр, загружаются справочники
 *  - «Aggregators» — список доступных агрегаторов по предприятию
 *  - «Brands» — список доступных брендов по предприятию
 *  - «Entities» — список доступных юр лиц по предприятию
 *  - «PaymentMethods» — список доступных способов оплаты по предприятию
 * 
 * Для удаления филиала из предприятия вызывается метод «FilialRemove» с ID филиала
 * 
 * Для получения филиала на редактирования выполняется запрос «Filial» по ID филиала
 * Каждый раз при открытии филиала на редактирование вызывается метод «FiscalCassaList» для получения подключенных фискальных касс
 * Для сохранения изменений по филиалу используется метод «FilialSave» с параметрами
 */

import React, { Component } from 'react';
import { observer } from "mobx-react";
import _ from 'lodash';

import { TextInput, CheckGroup, Selectize, CheckFilter } from '../forms/FormItems';

import { TableFilterPage } from './PageTemplates';
import { AddButton, ItemNewHeader, RemoveIconButton } from '../shared/PageComponents';
import { WorkDaysBlock } from '../shared/WorkDaysComponents';

import { buttonTitles, CassaTypes } from './../dictionary';

import { FilialState } from '../stores/filials';
import { uistate, confirmState, errorState, feedbackModel, authUserState } from '../stores/common';
import { toSelectList, toCheckList } from '../functions';
import { FilialApi, SystemApi, UserApi } from '../stores/apiMethods';
import { HelpIcon, StockIcon } from '../images/icons';

export class FilialsPage extends Component {
	constructor(props) {
		super(props);

		this.store = new FilialState();
	}

	componentDidMount() {
		this.store.setFilial();
		this.getData();

		SystemApi.getCities((data) => { this.store.Cities = data.Success ? data.Cities : []; });

		if (!authUserState.isCallCentre && authUserState.HasERP) {
			SystemApi.getBrands((data) => { this.store.Brands = data.Success ? data.Brands : []; });
			SystemApi.getEntities((data) => { this.store.Entities = data.Success ? data.Entities : []; });
			SystemApi.getAggregators((data) => { this.store.Aggregators = data.Success ? data.Aggregators : []; });
			SystemApi.getPaymentMethods((data) => { this.store.PaymentMethods = data.Success ? data.PaymentMethods : []; });
		}
	}

	/** API запрос «Filials» на получение списка филиалов */
	getData() {
		this.store.IsSendRequest = false;
		FilialApi.getList((data) => { this.store.setFilials(data); });
	}

	/**
	 * API запрос «Filial» на получение одного филиала по ID
	 * @param {number} id ID филиала
	 */
	getItem(id) {
		FilialApi.getById(id, (data) => { this.store.setFilial(data.Success ? data : undefined); });
	}

	/** API запрос «User» на получение авторизованного пользователя */
	getUser() {
		this.store.User = undefined;
		UserApi.getById(authUserState.User.Id, (data) => {
			this.store.User = data.Success ? _.omit(data, ["Success", "ErrorCode"]) : undefined;
		});
	}

	/**
	 * Выбор/сброс актинвого филиала
	 * @param {number} id ID филиала
	 */
	setId(id) { 
		if (!_.isUndefined(id)){ 
			if (id !== -1) this.getItem(id);
			else {
				this.getUser();
				this.store.setFilial({ Id : -1 });
			}
		} else {
			this.store.setFilial(undefined);
			this.getData();
		}
	}

	/**
	 * API запрос «FilialRemove» на удаление филиала по ID
	 * @param {number} id ID филиала
	 */
	removeId(id) { 
		if (_.isUndefined(id)) return false;

		var item = _.find(this.store.Filials, { Id : id }),
			text = <span>Вы действительно хотите удалить филиал <b key="b1">«{item ? item.Name : ""}»</b>?</span>;

		confirmState.openConfirm(text, () => { FilialApi.remove(id, () => { this.getData(); }); });
	}

	render() {
		return (
			<div className="page" id="FilialsPage">
				{_.isUndefined(this.store.Filial) && this.store.IsSendRequest &&
					<TableFilterPage 
						Type={this.props.Type}
						CanManage={this.store.CanManage}

						TableData={this.store.Filials}
						TableColumns={this.store.columns}
						TableFilterable={true}

						setId={this.setId.bind(this)}
						removeId={this.removeId.bind(this)}
					/>
				}
				{!_.isUndefined(this.store.Filial) &&
					<FilialItem store={this.store} setId={this.setId.bind(this)} />
				}
			</div>
		)
	}
}
FilialsPage = observer(FilialsPage);

class FilialItem extends Component {
	componentDidMount() {
		if (authUserState.HasERP)
			this.getCassaList();
	}

	/** API запрос «FiscalCassaList» на получение списка непривязанных касс */
	getCassaList () {
		FilialApi.getFiscalCassaList((data) => {
			this.props.store.CassaList = data.Success ? _.map(data.FiscalCassaList, (v) => {
				return { Id : v.Name, Name : v.Name }; }) : [];
			});
	}

	/**
	 * API запрос «UserSave» на добавление созданного филиала авторизованному пользователю
	 * @param {number} filialId ID созданного филиала
	 */
	saveUser(filialId) {
		const manualAccessText =
			"Проверьте, что вашему пользователю и нужным сотрудникам выдан доступ к новому филиалу.";

		if (_.isUndefined(this.props.store.User)) {
			errorState.setError(null, null, manualAccessText,
				"success", "Создан новый филиал");
			return;
		}

		const dashboards = this.props.store.User.Dashboards || {};
		const user = {
			..._.omit(this.props.store.User, "Dashboards"),
			Filials: _.union(this.props.store.User.Filials || [], [filialId])
		};

		_.each(dashboards, (list, type) => {
			user["Dashboards." + type] = (list || []).join(",");
		});

		UserApi.save(user, (data) => {
			if (data.Success) {
				this.props.store.User = user;
				errorState.setError(null, null,
					"Доступ к новому филиалу добавлен вашему пользователю.\n" +
					"Обновите страницу, чтобы филиал появился в списках.\n\n" +
					"Доступы для остальных сотрудников настройте отдельно.",
					"success", "Создан новый филиал");
			} else {
				errorState.setError(null, null, manualAccessText,
					"success", "Создан новый филиал");
			}
		});
	}

	/** API запрос «FilialSave» на редактирование филиала */
	handleSubmit (event) {
		event.preventDefault();

		if (!this.props.store.Filial.validate())
			return false;

		// console.log(this.props.store.Filial.getData())

		this.props.store.Filial.ButtonLoading = true;
		const isNew = this.props.store.Filial.Id === -1;
		const hasNewPOS = !isNew && _.some(this.props.store.Filial.POS, { Id : -1 });

		FilialApi.save(this.props.store.Filial.getData(), (data) => {
			this.props.store.Filial.ErrorCode = data.Success
				? isNew || hasNewPOS ? "" : "SUCCESS_SAVE"
				: data.ErrorCode;
			this.props.store.Filial.SuccessSave = data.Success;
			this.props.store.Filial.ButtonLoading = false;

			if (data.Success) {
				if (isNew) {
					this.props.store.Filial.Id = data.Id;
					this.saveUser(data.Id);
				} else if (hasNewPOS) {
					errorState.setError(null, null,
						"Добавьте доступ к ней нужным сотрудникам.",
						"success", "Добавлена новая точка продаж");
				}

				if (authUserState.HasERP) this.getCassaList();
			}
		});
	}

	/** Закрытие окна редактирование филиала */
	handleClose (event) {
		event.preventDefault();
		this.props.setId(undefined);
	}

	render() {
		return (
			<section className="item" id="FilialItem">
				<form onSubmit={this.handleSubmit.bind(this)} >
					<ItemNewHeader 
						Title={buttonTitles[(this.props.store.Filial.Id === -1 ? "New" : "Edit") + "Filial"]}
						ErrorCode={this.props.store.Filial.ErrorCode}
						ButtonLoading={this.props.store.Filial.ButtonLoading}
						CanManage={this.props.store.CanManage}
						SuccessSave={this.props.store.Filial.SuccessSave}
						handleClose={this.handleClose.bind(this)}
					/>
					<div className={this.props.store.Filial.isERP ? "withStocks" : ""}>
						<MainSettings store={this.props.store} />
						<WorkDaysBlock
							Title="Время работы филиала"
							WorkDays={this.props.store.Filial.WorkDays}
						>
							<p className='comment'>Для работы со сменами необходимо настроить часы работы филиала. Если у персонала есть задачи, после официального закрытия филиала настройте поле «Сдвиг смены»</p>
							<TextInput
								Id="ShiftHour"
								Type="number"
								Title="Сдвиг смены, часы"
								Value={this.props.store.Filial.ShiftHour}
								RowClass="shift"
								onChange={(value,id) => this.props.store.Filial.collect(id, value)}
							/>
						</WorkDaysBlock>
						{this.props.store.Filial.isERP && <StoreBlock store={this.props.store} />}
					</div>
					{this.props.store.Filial.isERP && <PosBlock store={this.props.store} />}
				</form>
			</section>
		)
	}
}
FilialItem = observer(FilialItem);

/** Блок с основными настройками */
class MainSettings extends Component {
	render() {
		return(
			<div className="mainInfo section">
				<div>
					<TextInput
						Id="Name"
						Title={"Филиал " + (this.props.store.Filial.Id !== -1 ? "ID: " + this.props.store.Filial.Id : "")}
						Value={this.props.store.Filial.Name}
						Required={true}
						onChange={(value,id) => this.props.store.Filial.collect(id, value)}
					/>
					<TextInput
						Id="Address"
						Title="Адрес"
						Value={this.props.store.Filial.Address}
						onChange={(value,id) => this.props.store.Filial.collect(id, value)}
					/>
				</div>
				<div>
					<Selectize 
						Id="CityId"
						Title={"Город" + this.props.store.Filial.TimeZoneTitle}
						List={toSelectList(this.props.store.Cities)}
						Value={this.props.store.Filial.CityId}
						Required={true}
						onChange={(value,id) => this.props.store.Filial.collect(id, value)}
					/>
					<TextInput
						Id="Phone"
						Title="Телефон"
						Value={this.props.store.Filial.Phone}
						Mask="7 (999) 999-99-99"
						onChange={(value,id) => this.props.store.Filial.collect(id, value)}
					/>
				</div>
			</div>
		)
	}
}
MainSettings = observer(MainSettings);

class StoreBlock extends Component {
	render() {
		return(
			<div className="section stocks">
				<h4 className="sectionTitle"><StockIcon />{buttonTitles.Stores}</h4>
				{_.map(this.props.store.Filial.Stocks, (item,i) => {
					return (<StoreItem Filial={this.props.store.Filial} Item={item} Index={i} key={"st_" + i} />)
				}) }
				<AddButton Title={buttonTitles.AddStore} onClick={() => this.props.store.Filial.addStock()} />
			</div>
		)
	}
}
StoreBlock = observer(StoreBlock);

class StoreItem extends Component {
	render() {
		var stockList = [{ Id : "Kitchen", Title : "Кухня"}, { Id : "Bar", Title : "Бар"}],
			menu = [];

		if (this.props.Filial.Kitchen === this.props.Item.Id) menu = _.concat(menu, "Kitchen");
		if (this.props.Filial.Bar === this.props.Item.Id) menu = _.concat(menu, "Bar");

		return(
			<div className="stock">
				<TextInput
					Id="Name"
					Title="Название"
					Value={this.props.Item.Name}
					Required={true}
					onChange={(value, id) => this.props.Item.collect(id, value)}
				/>
				<CheckGroup
					Id="Menu"
					Title="Списание продукции"
					Type="checkbox"
					RowClass="menu"
					List={stockList}
					Value={menu}
					onChange={(value, id) => this.props.Filial.collectStockType(id, value, this.props.Index)}
				/>
				<RemoveIconButton onClick={() => this.props.Filial.removeStock(this.props.Index)} />
			</div>
		)
	}
}
StoreItem = observer(StoreItem);

class PosBlock extends Component {
	render() {
		return(
			<>
				<h4>Точки продаж</h4>
					{_.map(this.props.store.Filial.POS, (item,i) => {
						return (<PosItem store={this.props.store} Item={item} Index={i} key={"PS_" + i} />)
					}) }
				<AddButton Title={buttonTitles.AddPOS} onClick={() => this.props.store.Filial.addPOS()} />
			</>
		)
	}
}
PosBlock = observer(PosBlock)

class PosItem extends Component {
	render() {
		return(
			<div className={"section pos " + (uistate.IsMobile ? "mobile" : "")}>
				<PosMain
					Filial={this.props.store.Filial}
					Entities={this.props.store.Entities}
					Item={this.props.Item}
					Index={this.props.Index}
				/>
				<div className='settings'>
					<PosCassa CassaList={this.props.store.CassaList} Item={this.props.Item} />
					<PosAvailable
						Brands={this.props.store.Brands}
						Aggregators={this.props.store.Aggregators}
						PaymentMethods={this.props.store.PaymentMethods}
						Item={this.props.Item}
					/>
				</div>
				<div className="types">
					{_.map(this.props.Item.Types, (v, i) => {
						return(<PosTypeItem key={i} Item={v} />)
					})}
				</div>
			</div>
		)
	}
}
PosItem = observer(PosItem);

class PosMain extends Component {
	render() {
		return(
			<div className='main'>
				<TextInput
					Id="Name"
					Title={"Название " + (this.props.Item.Id !== -1 ? "ID: " + this.props.Item.Id : "")}
					Value={this.props.Item.Name}
					Required={true}
					onChange={(value, id) => this.props.Item.collect(id, value)}
				/>
				<Selectize
					Id="EntityId"
					Title="Юр. лицо"
					List={toSelectList(this.props.Entities)}
					Value={this.props.Item.EntityId}
					isClearable={false}
					onChange={(value, id) => this.props.Item.collect(id, value)}
				/>
				<CheckFilter
					Id="MainPOS"
					Title="Основная точка"
					Value={this.props.Index === this.props.Filial.MainPOS}
					onChange={(value, id) => this.props.Filial.collect(id, value ? this.props.Index : -1)}
				/>
				<RemoveIconButton onClick={() => this.props.Filial.removePOS(this.props.Index)} />
			</div>
		)
	}
}
PosMain = observer(PosMain);

class PosCassa extends Component {
	render() {
		var cassaName = this.props.Item.FiscalCassaName,
			cassaList = cassaName === "" ? this.props.CassaList :
				_.concat(_.clone(this.props.CassaList), { Id : cassaName, Name : cassaName });

		return(
			<div className="cassa">
				<h5>Настройки кассы</h5>
				<CheckFilter
					Id="HasCassa"
					Title="Отдельная касса"
					RowClass="nomargin"
					Value={this.props.Item.HasCassa}
					onChange={(value, id) => this.props.Item.collect(id, value)}
				/>
				{this.props.Item.HasCassa &&
					<CheckFilter
						Id="Fiscal"
						Title="Фискальная касса"
						RowClass="nomargin"
						Value={this.props.Item.Fiscal}
						onChange={(value, id) => this.props.Item.collect(id, value)}
					/>
				}
				{this.props.Item.HasCassa && this.props.Item.Fiscal &&
					<>
						<Selectize
							Id="CassaType"
							Title="Тип кассы"
							List={toSelectList(CassaTypes)}
							Value={this.props.Item.CassaType}
							onChange={(value, id) => this.props.Item.collect(id, value)}
						/>
						<div className="fiscalCassaField">
							<Selectize
								Id="FiscalCassaName"
								Title="Ключ фискальной кассы"
								List={toSelectList(cassaList)}
								Value={this.props.Item.FiscalCassaName}
								onChange={(value, id) => this.props.Item.collect(id, value)}
							/>
							<a className="fiscalCassaHelp iconSvg" href="/"
								title="Инструкция по настройке кассы"
								onClick={(event) => {
									event.preventDefault();
									feedbackModel.openModal(
										"Инструкция по настройке кассы",
										<FiscalCassaInstruction />
									);
								}}>
								<HelpIcon />
							</a>
						</div>
					</>
				}
			</div>
		)
	}
}
PosCassa = observer(PosCassa);

export const FiscalCassaInstruction = () =>
	<div className="fiscalCassaInstruction">
		<ol>
			<li>Получите файл конфигурации кассового приложения у администратора системы.</li>
			<li>Подключите кассу к устройству.</li>
			<li>Запустите файл конфигурации кассового приложения.</li>
			<li>Дождитесь завершения выполнения скрипта.</li>
			<li>Переоткройте карточку филиала и выберите кассу в списке.</li>
		</ol>
		<p>
			Если касса уже использовалась в другом предприятии, в конфигурации кассового
			приложения должен быть указан ключ текущего предприятия. Актуальный ключ
			находится в поле <code>merchant.cassasecret</code>.
		</p>
		<p>
			После изменения ключа перезапустите кассовое приложение и привяжите кассу
			к текущему предприятию.
		</p>
	</div>;

class PosAvailable extends Component {
	render() {
		return(
			<div className="available">
				{/* TODO: заменить выбор брендов, агрегаторов и способов оплаты на общий CheckBoxGroup. */}
				<CheckGroup
					Id="Brands"
					Title="Бренды"
					Type="checkbox"
					List={toCheckList(this.props.Brands)}
					Value={this.props.Item.Brands}
					onChange={(value, id) => this.props.Item.collect(id, value)}
				/>
				<CheckGroup
					Id="Aggregators"
					Title="Агрегаторы"
					Type="checkbox"
					List={toCheckList(this.props.Aggregators)}
					Value={this.props.Item.Aggregators}
					onChange={(value, id) => this.props.Item.collect(id, value)}
				/>
				<CheckGroup
					Id="PaymentMethods"
					Title="Способы оплаты"
					Type="checkbox"
					List={toCheckList(this.props.PaymentMethods)}
					Value={this.props.Item.PaymentMethods}
					onChange={(value, id) => this.props.Item.collect(id, value)}
				/>
			</div>
		)
	}
}
PosAvailable = observer(PosAvailable);

class PosTypeItem extends Component {
	render() {
		return(
			<div className="type">
				<CheckFilter
					Id="Active"
					Title={this.props.Item.Name}
					RowClass="nomargin"
					Value={this.props.Item.Active}
					onChange={(value, id) => this.props.Item.collect(id, value)}
				/>
				<TextInput
					Id="FirstTime"
					Title="Со скольких можно оформлять текущий заказ"
					Type="number"
					Value={this.props.Item.FirstTime}
					Disabled={!this.props.Item.Active}
					Required={this.props.Item.Active}
					onChange={(value, id) => this.props.Item.collect(id, value)}
				/>
				<TextInput
					Id="FirstToTime"
					Title="Ко скольким можно начать оформлять заказ ко времени"
					Type="number"
					Value={this.props.Item.FirstToTime}
					Disabled={!this.props.Item.Active}
					Required={this.props.Item.Active}
					onChange={(value, id) => this.props.Item.collect(id, value)}
				/>
				<TextInput
					Id="LastTime"
					Title="До скольких можно оформлять текущий заказ"
					Type="number"
					Value={this.props.Item.LastTime}
					Disabled={!this.props.Item.Active}
					Required={this.props.Item.Active}
					onChange={(value, id) => this.props.Item.collect(id, value)}
				/>
				<TextInput
					Id="LastToTime"
					Title="До скольких можно забрать/доставить заказ ко времени"
					Type="number"
					Value={this.props.Item.LastToTime}
					Disabled={!this.props.Item.Active}
					Required={this.props.Item.Active}
					onChange={(value, id) => this.props.Item.collect(id, value)}
				/>
			</div>
		)
	}
}
PosTypeItem = observer(PosTypeItem);
